import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { resolveMediaUrl } from '@/utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Default branding values
const defaultBranding = {
  logo_light: null,       // Full logo for light backgrounds (recommended: 200x50px)
  logo_dark: null,        // Full logo for dark backgrounds (recommended: 200x50px)
  logo_icon: null,        // Square icon for collapsed sidebar (recommended: 40x40px)
  logo_auth: null,        // Large logo for auth pages (recommended: 300x80px)
  favicon: null,          // Favicon (recommended: 32x32px or 64x64px)
  site_name: 'Provisoft',
  tagline: 'Complete ERP solution for Australian NDIS providers',
  primary_color: '#6366f1', // indigo-600
  secondary_color: '#7c3aed', // purple-600
};

// Logo size recommendations (for display in admin)
export const LOGO_SIZE_RECOMMENDATIONS = {
  logo_light: { width: 200, height: 50, label: 'Full Logo (Light)', description: 'Used in sidebar when expanded. Recommended: 200x50px (PNG with transparency)' },
  logo_dark: { width: 200, height: 50, label: 'Full Logo (Dark)', description: 'Used in sidebar on dark mode. Recommended: 200x50px (PNG with transparency)' },
  logo_icon: { width: 40, height: 40, label: 'Icon/Square Logo', description: 'Used in collapsed sidebar. Recommended: 40x40px (Square, PNG with transparency)' },
  logo_auth: { width: 300, height: 80, label: 'Auth Page Logo', description: 'Used on login/signup pages. Recommended: 300x80px (PNG with transparency)' },
  favicon: { width: 64, height: 64, label: 'Favicon', description: 'Browser tab icon. Recommended: 64x64px (PNG or ICO)' },
};

// Create context
const BrandContext = createContext({
  branding: defaultBranding,
  loading: true,
  error: null,
  refetch: () => {},
});

// Provider component
export const BrandProvider = ({ children }) => {
  const sessionBrand = typeof window !== 'undefined' ? window.__APP_SESSION__?.brand : null;
  const [branding, setBranding] = useState(() =>
    sessionBrand ? { ...defaultBranding, ...sessionBrand } : defaultBranding
  );
  const [loading, setLoading] = useState(!sessionBrand);
  const [error, setError] = useState(null);

  const fetchBranding = useCallback(async ({ force = false } = {}) => {
    const seeded = window.__APP_SESSION__?.brand;
    if (seeded && !force) {
      setBranding({ ...defaultBranding, ...seeded });
      setLoading(false);
      setError(null);
      return;
    }
    try {
      if (!seeded) setLoading(true);
      const response = await axios.get(`${API}/site-settings/brand`);
      setBranding({
        ...defaultBranding,
        ...response.data,
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch branding:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding({ force: false });
  }, [fetchBranding]);

  // Update document title and favicon when branding changes
  useEffect(() => {
    if (!loading && branding) {
      // Update favicon if available (all icon links — static + dynamic)
      if (branding.favicon) {
        const href = resolveMediaUrl(branding.favicon) || branding.favicon;
        const links = document.querySelectorAll("link[rel*='icon']");
        if (links.length) {
          links.forEach((el) => {
            el.href = href;
          });
        } else {
          const link = document.createElement('link');
          link.rel = 'icon';
          link.href = href;
          document.head.appendChild(link);
        }
      }
      
      // Set default document title from branding
      const currentTitle = document.title;
      if (currentTitle === 'Loading...' || currentTitle === '' || currentTitle === 'Emergent | Fullstack App') {
        const siteName = branding.site_name || 'Provisoft';
        const tagline = branding.tagline || 'Workforce Management Platform';
        document.title = `${siteName} - ${tagline}`;
      }
    }
  }, [branding, loading]);

  const value = useMemo(
    () => ({
      branding,
      loading,
      error,
      refetch: () => fetchBranding({ force: true }),
    }),
    [branding, loading, error, fetchBranding]
  );

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};

// Custom hook for consuming branding
export const useBranding = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandProvider');
  }
  return context;
};

// Full Logo component (for expanded sidebar, headers)
export const BrandLogo = ({ 
  variant = 'light', // 'light' or 'dark'
  className = '',
  size = 'md', // 'sm', 'md', 'lg'
  imgClassName = '', // optional per-usage override for the <img> (e.g. sidebar)
}) => {
  const { branding } = useBranding();
  
  // Height classes for uploaded logos
  const logoHeightClasses = {
    sm: 'h-7',
    md: 'h-8',
    lg: 'h-10',
  };
  
  // Fallback icon sizes
  const iconSizeClasses = {
    sm: 'w-7 h-7',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };
  
  const textSizeClasses = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  const logoUrl = variant === 'dark' 
    ? (branding.logo_dark || branding.logo_light) 
    : (branding.logo_light || branding.logo_dark);
  const siteName = branding.site_name || 'Provisoft';
  const resolvedLogoUrl = resolveMediaUrl(logoUrl);

  // If logo URL exists, show ONLY the logo (no text)
  if (resolvedLogoUrl) {
    return (
      <div className={`flex items-center ${className}`}>
        <img 
          src={resolvedLogoUrl} 
          alt={siteName} 
          className={`${logoHeightClasses[size]} w-auto object-contain max-w-[160px] ${imgClassName}`}
        />
      </div>
    );
  }

  // Fallback to default logo (icon + text) when no logo is uploaded
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className={`${iconSizeClasses[size]} bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0`}>
        <span className="text-white font-bold" style={{ fontSize: size === 'sm' ? '0.875rem' : size === 'md' ? '1rem' : '1.25rem' }}>
          {siteName.charAt(0)}
        </span>
      </div>
      <span className={`${textSizeClasses[size]} font-heading font-bold text-foreground truncate`}>
        {siteName}
      </span>
    </div>
  );
};

// Icon Logo component (for collapsed sidebar)
export const BrandIcon = ({ 
  className = '',
  size = 'md', // 'sm', 'md', 'lg'
}) => {
  const { branding } = useBranding();
  
  const sizeClasses = {
    sm: 'w-7 h-7',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };
  
  const fontSizeClasses = {
    sm: '0.875rem',
    md: '1rem',
    lg: '1.25rem',
  };

  const siteName = branding.site_name || 'Provisoft';

  // If icon URL exists, use it
  if (branding.logo_icon) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <img 
          src={resolveMediaUrl(branding.logo_icon)} 
          alt={siteName} 
          className={`${sizeClasses[size]} object-contain`}
        />
      </div>
    );
  }

  // Fallback to first letter icon
  return (
    <div className={`${sizeClasses[size]} bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 ${className}`}>
      <span className="text-white font-bold" style={{ fontSize: fontSizeClasses[size] }}>
        {siteName.charAt(0)}
      </span>
    </div>
  );
};

// Auth Page Logo component (larger logo for login/signup pages)
export const BrandAuthLogo = ({ 
  className = '',
  size = 'lg', // 'md', 'lg', 'xl'
}) => {
  const { branding } = useBranding();
  
  // Height classes for uploaded logos
  const logoHeightClasses = {
    md: 'h-10',
    lg: 'h-14',
    xl: 'h-20',
  };
  
  // Fallback icon sizes
  const iconSizeClasses = {
    md: 'w-10 h-10',
    lg: 'w-14 h-14',
    xl: 'w-20 h-20',
  };
  
  const textSizeClasses = {
    md: 'text-2xl',
    lg: 'text-3xl',
    xl: 'text-4xl',
  };

  const siteName = branding.site_name || 'Provisoft';
  
  // Priority: logo_auth -> logo_light -> logo_dark
  const logoUrl = branding.logo_auth || branding.logo_light || branding.logo_dark;
  const resolvedLogoUrl = resolveMediaUrl(logoUrl);

  // If logo URL exists, show ONLY the logo (no text)
  if (resolvedLogoUrl) {
    return (
      <div className={`flex items-center ${className}`}>
        <img 
          src={resolvedLogoUrl} 
          alt={siteName} 
          className={`${logoHeightClasses[size]} w-auto object-contain max-w-[280px]`}
        />
      </div>
    );
  }

  // Fallback to default logo (icon + text) when no logo is uploaded
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className={`${iconSizeClasses[size]} bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0`}>
        <span className="text-white font-bold" style={{ fontSize: size === 'md' ? '1.25rem' : size === 'lg' ? '1.5rem' : '2rem' }}>
          {siteName.charAt(0)}
        </span>
      </div>
      <span className={`${textSizeClasses[size]} font-heading font-bold text-foreground truncate`}>
        {siteName}
      </span>
    </div>
  );
};

export default BrandContext;
